import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';
import { sisterConcerns } from '@/mocks/homeData';

const categoryIcons: Record<string, string> = {
  garments: 'ri-t-shirt-line',
  accessories: 'ri-vip-diamond-line',
  'printing-dyeing': 'ri-palette-line',
  packaging: 'ri-archive-line',
  'washing-laundry': 'ri-drop-line',
};

const flowSteps = [
  { icon: 'ri-t-shirt-line', label: 'Garments', desc: 'Core apparel production at BA Knit Composite', color: 'bg-primary-50 text-primary-600' },
  { icon: 'ri-vip-diamond-line', label: 'Accessories', desc: 'Trims & components from Beaumonde Accessories', color: 'bg-accent-50 text-accent-600' },
  { icon: 'ri-palette-line', label: 'Print & Dye', desc: 'Surface design at Modern Print & Dye', color: 'bg-secondary-50 text-secondary-600' },
  { icon: 'ri-drop-line', label: 'Washing', desc: 'Finishing at Clean Wash Laundry', color: 'bg-primary-50 text-primary-600' },
  { icon: 'ri-archive-line', label: 'Packaging', desc: 'Export-ready packing from Pak Pro', color: 'bg-accent-50 text-accent-600' },
];

const networkBenefits = [
  {
    icon: 'ri-link-m',
    title: 'Seamless Coordination',
    description: 'All five facilities operate under unified management, ensuring smooth handoffs between production stages and eliminating supply chain friction.',
  },
  {
    icon: 'ri-time-line',
    title: 'Faster Lead Times',
    description: 'Vertical integration means no waiting on third-party vendors. Accessories, printing, washing, and packaging all happen in-house — on your schedule.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Consistent Quality',
    description: 'One quality standard across the entire network. Every facility follows the same compliance protocols, testing procedures, and buyer specifications.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Cost Efficiency',
    description: 'Eliminate middlemen markups on trims, printing, washing, and packaging. Pass savings directly to your bottom line with group-level pricing.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Full Transparency',
    description: 'Track your order across every stage of production with a single point of contact. No more coordinating between five different vendors.',
  },
  {
    icon: 'ri-global-line',
    title: 'Export Ready',
    description: 'Every facility is export-compliant with international certifications. Your finished goods ship directly from our integrated campus.',
  },
];

export default function SisterConcernsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const totalWorkforce = sisterConcerns.reduce((sum, c) => sum + parseInt(c.stats[1].value.toString(), 10), 0);
  const totalLines = sisterConcerns.reduce((sum, c) => sum + parseInt(c.stats[2].value.toString(), 10), 0);
  const totalSqFt = sisterConcerns.reduce((sum, c) => sum + parseInt(c.stats[0].value.toString(), 10), 0);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[400px] md:h-[480px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20modern%20industrial%20complex%20with%20multiple%20factory%20buildings%20connected%20by%20landscaped%20pathways%2C%20clean%20architectural%20design%2C%20blue%20sky%20with%20soft%20clouds%2C%20bright%20natural%20daylight%2C%20corporate%20industrial%20photography%2C%20organized%20manufacturing%20campus&width=1600&height=800&seq=sister-hero-01&orientation=landscape)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-white/70 mb-4">
            Beaumonde Tex Group
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Sister Concerns
          </h1>
          <p className="max-w-[600px] mx-auto text-base md:text-lg text-white/80 leading-relaxed">
            A vertically integrated network of manufacturing facilities delivering end-to-end apparel solutions under one group.
          </p>
        </div>
      </section>

      {/* Network Stats Banner */}
      <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-3">
                <i className="ri-building-4-line text-primary-500 text-lg"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{totalSqFt}K</div>
              <div className="text-xs text-foreground-500 mt-1">Total Sq. Ft.</div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-50 mb-3">
                <i className="ri-team-line text-accent-500 text-lg"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{totalWorkforce.toLocaleString()}+</div>
              <div className="text-xs text-foreground-500 mt-1">Total Workforce</div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-50 mb-3">
                <i className="ri-settings-line text-secondary-500 text-lg"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{totalLines}</div>
              <div className="text-xs text-foreground-500 mt-1">Production Lines</div>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-3">
                <i className="ri-stack-line text-primary-500 text-lg"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">5</div>
              <div className="text-xs text-foreground-500 mt-1">Facilities</div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="pt-20 pb-14 md:pt-28 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-[720px] mx-auto text-center animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              Our Network
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 leading-tight">
              Five Specialized Facilities, One Unified Vision
            </h2>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
              The Beaumonde Tex group operates a vertically integrated ecosystem of manufacturing facilities, each specializing in a critical link of the apparel supply chain. From garment production and accessories to printing, packaging, and finishing — our sister concerns work in seamless coordination to deliver complete manufacturing solutions under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* Supply Chain Flow */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              How It Works
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              From Raw Material to Export
            </h2>
            <p className="text-foreground-500 max-w-[500px] mx-auto mt-3 text-sm">
              Our integrated facilities handle every stage — seamlessly connected, centrally managed.
            </p>
          </div>

          {/* Desktop flow */}
          <div className="hidden lg:flex items-start justify-between gap-4 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            {flowSteps.map((step, i) => (
              <div key={step.label} className="flex flex-col items-center text-center flex-1">
                <div className={`w-16 h-16 flex items-center justify-center rounded-full ${step.color} mb-4 relative`}>
                  <i className={`${step.icon} text-2xl`}></i>
                  {i < flowSteps.length - 1 && (
                    <div className="absolute top-1/2 -right-[calc(50%+0.5rem)] w-[calc(100%+1rem)] h-[2px]">
                      <div className="w-full h-full bg-gradient-to-r from-primary-200 via-accent-200 to-transparent"></div>
                    </div>
                  )}
                </div>
                <span className="font-heading text-sm font-semibold text-foreground-800 mb-2">{step.label}</span>
                <p className="text-xs text-foreground-500 leading-relaxed max-w-[160px]">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile/Tablet flow */}
          <div className="lg:hidden flex flex-col gap-4 max-w-[400px] mx-auto">
            {flowSteps.map((step, i) => (
              <div
                key={step.label}
                className="flex items-center gap-4 bg-white rounded-lg p-4 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-full ${step.color} flex-shrink-0`}>
                  <i className={`${step.icon} text-lg`}></i>
                </div>
                <div>
                  <span className="font-heading text-sm font-semibold text-foreground-800">{step.label}</span>
                  <p className="text-xs text-foreground-500 leading-relaxed mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sister Concern Cards */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              Our Facilities
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Explore Each Facility
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sisterConcerns.map((company, i) => (
              <div
                key={company.slug}
                className="group bg-white rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-primary-200 hover:-translate-y-1 cursor-pointer"
                style={{ transitionDelay: `${i * 100}ms` }}
                onClick={() => navigate(`/sister-concerns/${company.slug}`)}
              >
                <div className="h-[220px] overflow-hidden relative">
                  <img
                    src={company.heroImage}
                    alt={company.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-white/90 text-foreground-800 text-[11px] font-semibold font-heading">
                      {company.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {company.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-5 line-clamp-3">
                    {company.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {company.stats.slice(0, 3).map((stat) => (
                      <span
                        key={stat.label}
                        className="inline-flex items-center gap-1.5 text-[11px] text-foreground-500 bg-background-100 px-2.5 py-1.5 rounded-md"
                      >
                        <span className="font-semibold text-foreground-700">
                          {stat.value}{stat.suffix}
                        </span>
                        <span>{stat.label}</span>
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    View Details
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Network */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
              The Advantage
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Why Choose Our Network
            </h2>
            <p className="text-foreground-500 max-w-[500px] mx-auto mt-3 text-sm">
              Vertical integration isn&apos;t just a buzzword — it&apos;s how we deliver better results, faster.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {networkBenefits.map((benefit, i) => (
              <div
                key={benefit.title}
                className="bg-white rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-primary-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mb-4">
                  <i className={`${benefit.icon} text-accent-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-primary-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Integrated Manufacturing. Seamless Delivery.
          </h2>
          <p className="text-white/80 max-w-[550px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            Our sister concerns work together to give you a complete, coordinated supply chain — from raw materials to finished garments, all under one trusted group.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-primary-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Partner With Us
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/about')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              About the Group
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}